import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, interval, of, startWith, switchMap, takeWhile } from 'rxjs';
import {
  DevProdSyncJob,
  DevProdSyncProgress,
  DevProdSyncReadiness,
  DevProdSyncService,
} from '../../core/services/dev-prod-sync';
import { ToastService } from '../../core/services/toast';

const COPY: {
  title: string;
  warningTitle: string;
  warningItems: string[];
  progressTitle: string;
  loadingReadiness: string;
  readinessReady: string;
  readinessBlocked: string;
  disabledDeployment: string;
  missingEnvTitle: string;
  targetGuardTitle: string;
  statusLabel: string;
  noPendingJob: string;
  dryRunComplete: string;
  applyComplete: string;
  blocked: string;
  failed: string;
  summaryTitle: string;
  hardConflict: (count: number) => string;
  loadManifest: string;
  loadingManifest: string;
  historyTitle: string;
  close: string;
  dryRun: string;
  dryRunStarting: string;
  apply: string;
  applyStarting: string;
  retryReadiness: string;
  readinessGenericError: string;
  readinessUnauthorized: string;
  readinessForbidden: string;
  readinessNotFound: string;
  readinessNetwork: string;
  readinessHtmlFallback: string;
  dryRunNeedsReadiness: string;
  dryRunLoadingReadiness: string;
  dryRunDisabledDeployment: string;
  dryRunNotReady: string;
  dryRunRunningJob: string;
  dryRunStartError: string;
  applyStartError: string;
  manifestError: string;
  dryRunEnabledToast: string;
  jobIncomplete: string;
} = {
  title: 'Sync dev data to prod',
  warningTitle: 'Warning: This action always runs dev → prod.',
  warningItems: [
    'It moves MongoDB and MinIO content from dev to prod in append-only mode.',
    'Records with the same identity are not moved again; existing prod data is never updated or deleted.',
    'Dev app_id values are rewritten to the matching production app_id; ambiguous matches appear as conflicts in the dry-run manifest.',
    'Records that look the same but have different IDs/identities may create duplicate content in prod; review the dry-run manifest.',
    'Real sync cannot run while hard conflicts exist.',
    'Dry-run never writes to prod MongoDB or MinIO; it only writes a manifest JSON file with planned changes and fingerprints.',
  ],
  progressTitle: 'Progress',
  loadingReadiness: 'Checking readiness…',
  readinessReady: 'Sync API is ready.',
  readinessBlocked: 'Sync API is not ready.',
  disabledDeployment: 'Dev→prod sync is disabled for this deployment. The dev backend is not apply-capable.',
  missingEnvTitle: 'Missing env',
  targetGuardTitle: 'Target guard errors',
  statusLabel: 'Status',
  noPendingJob: 'No pending job.',
  dryRunComplete: 'Dry-run completed.',
  applyComplete: 'Sync completed.',
  blocked: 'Job stopped because of hard conflicts.',
  failed: 'Job failed.',
  summaryTitle: 'Summary',
  hardConflict: count => `${count} hard conflicts exist. Real sync stays disabled.`,
  loadManifest: 'View manifest',
  loadingManifest: 'Loading manifest…',
  historyTitle: 'Recent jobs',
  close: 'Close',
  dryRun: 'Test with dry-run',
  dryRunStarting: 'Starting dry-run…',
  apply: 'Real sync / Apply',
  applyStarting: 'Starting sync…',
  retryReadiness: 'Retry readiness',
  readinessGenericError: 'Could not load sync readiness.',
  readinessUnauthorized: 'Session could not be authenticated. Sign in again and retry.',
  readinessForbidden: 'This action requires super_admin permission.',
  readinessNotFound: 'Dev→prod sync API route was not found. Check backend deploy/proxy.',
  readinessNetwork: 'Sync API is unreachable. Check dev backend/proxy access.',
  readinessHtmlFallback: 'Sync API returned HTML instead of JSON. /api/v1/dev-prod-sync may be hitting the frontend fallback; check the frontend/backend reverse proxy.',
  dryRunNeedsReadiness: 'Readiness must pass before dry-run can start.',
  dryRunLoadingReadiness: 'Dry-run cannot start until the readiness check finishes.',
  dryRunDisabledDeployment: 'Dev→prod sync is disabled for this deployment.',
  dryRunNotReady: 'Missing env or target guard errors must be fixed before dry-run.',
  dryRunRunningJob: 'Wait for the running job to finish before starting another dry-run.',
  dryRunStartError: 'Could not start dry-run.',
  applyStartError: 'Could not start real sync.',
  manifestError: 'Could not load manifest.',
  dryRunEnabledToast: 'Dry-run completed. Real sync is enabled.',
  jobIncomplete: 'Sync job could not complete.',
};

@Component({
  selector: 'app-dev-prod-sync-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dev-prod-sync-modal.html',
  styleUrl: './dev-prod-sync-modal.css',
})
export class DevProdSyncModalComponent implements OnInit {
  private readonly syncService = inject(DevProdSyncService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly copy = COPY;

  @Output() closeModal = new EventEmitter<void>();

  readiness = signal<DevProdSyncReadiness | null>(null);
  readinessError = signal<string | null>(null);
  jobs = signal<DevProdSyncJob[]>([]);
  activeJob = signal<DevProdSyncJob | null>(null);
  cleanDryRun = signal<DevProdSyncJob | null>(null);
  manifestJson = signal<string | null>(null);
  isLoadingReadiness = signal(false);
  isStartingDryRun = signal(false);
  isStartingApply = signal(false);
  isLoadingManifest = signal(false);

  private polledJobId: string | null = null;

  ngOnInit(): void {
    this.loadReadiness();
    this.loadJobs();
  }

  get canStartDryRun(): boolean {
    const readiness = this.readiness();
    return !!readiness?.enabled && !!readiness?.ready && !this.hasRunningJob() && !this.isStartingDryRun();
  }

  get canApply(): boolean {
    const dryRun = this.cleanDryRun();
    return !!dryRun?.apply_eligible && !this.hasRunningJob() && !this.isStartingApply();
  }

  get statusText(): string {
    const active = this.activeJob();
    if (!active) return this.copy.noPendingJob;
    if (active.mode === 'dry-run' && active.status === 'completed') return this.copy.dryRunComplete;
    if (active.mode === 'apply' && active.status === 'completed') return this.copy.applyComplete;
    if (active.status === 'blocked') return this.copy.blocked;
    if (active.status === 'failed') return this.copy.failed;
    return `${active.mode} ${active.status}…`;
  }

  get dryRunDisabledReason(): string | null {
    if (this.canStartDryRun) return null;
    if (this.hasRunningJob()) return this.copy.dryRunRunningJob;
    if (this.isLoadingReadiness()) return this.copy.dryRunLoadingReadiness;
    if (this.readinessError()) return this.copy.dryRunNeedsReadiness;
    const readiness = this.readiness();
    if (!readiness) return this.copy.dryRunLoadingReadiness;
    if (!readiness.enabled) return this.copy.dryRunDisabledDeployment;
    if (!readiness.ready) return this.copy.dryRunNotReady;
    return null;
  }

  loadReadiness(): void {
    this.isLoadingReadiness.set(true);
    this.readinessError.set(null);
    this.syncService.getReadiness().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: readiness => {
        this.readiness.set(readiness);
        this.isLoadingReadiness.set(false);
      },
      error: err => {
        this.readinessError.set(this.readinessErrorMessage(err));
        this.isLoadingReadiness.set(false);
      },
    });
  }

  loadJobs(): void {
    this.syncService.listJobs(10).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        const jobs = res.jobs ?? [];
        this.jobs.set(jobs);
        this.refreshCleanDryRun(jobs);
        this.resumeRunningJob(jobs);
      },
      error: () => {
        this.jobs.set([]);
        this.cleanDryRun.set(null);
      },
    });
  }

  startDryRun(): void {
    if (!this.canStartDryRun) return;
    this.isStartingDryRun.set(true);
    this.manifestJson.set(null);
    this.syncService.createJob({ mode: 'dry-run' }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: job => {
        this.isStartingDryRun.set(false);
        this.activeJob.set(job);
        this.pollJob(job.job_id);
      },
      error: err => {
        this.isStartingDryRun.set(false);
        this.toast.show(err?.error?.detail ?? this.copy.dryRunStartError, 'error', 6000);
        if (err?.status === 409) this.loadJobs();
      },
    });
  }

  startApply(): void {
    const dryRun = this.cleanDryRun();
    if (!this.canApply || !dryRun) return;
    this.isStartingApply.set(true);
    this.manifestJson.set(null);
    this.syncService.createJob({ mode: 'apply', dry_run_job_id: dryRun.job_id }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: job => {
        this.isStartingApply.set(false);
        this.activeJob.set(job);
        this.pollJob(job.job_id);
      },
      error: err => {
        this.isStartingApply.set(false);
        this.toast.show(err?.error?.detail ?? this.copy.applyStartError, 'error', 6000);
      },
    });
  }

  loadManifest(job: DevProdSyncJob): void {
    if (!job.manifest_digest) return;
    this.isLoadingManifest.set(true);
    this.syncService.getManifest(job.job_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.manifestJson.set(JSON.stringify(res.manifest, null, 2));
        this.isLoadingManifest.set(false);
      },
      error: err => {
        this.toast.show(err?.error?.detail ?? this.copy.manifestError, 'error', 6000);
        this.isLoadingManifest.set(false);
      },
    });
  }

  summaryEntries(job: DevProdSyncJob | null): { key: string; value: number }[] {
    if (!job?.summary) return [];
    return Object.entries(job.summary).map(([key, value]) => ({ key, value }));
  }

  hardConflictCount(job: DevProdSyncJob | null): number {
    return Number(job?.summary?.['hard_conflicts'] ?? 0);
  }

  progressPercent(job: DevProdSyncJob | null): number {
    const raw = Number(job?.progress?.percent ?? 0);
    if (!Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(100, raw));
  }

  progressDetail(progress: DevProdSyncProgress | null | undefined): string | null {
    if (!progress) return null;
    const parts: string[] = [];
    if (progress.total != null && progress.processed != null && progress.total > 0) {
      parts.push(`${progress.processed}/${progress.total}`);
    }
    if (progress.namespace) parts.push(progress.namespace);
    if (progress.bucket) parts.push(`bucket: ${progress.bucket}`);
    return parts.length ? parts.join(' · ') : null;
  }

  hasRunningJob(): boolean {
    const active = this.activeJob();
    return (!!active && this.isRunningJob(active)) || this.jobs().some(job => this.isRunningJob(job));
  }

  selectJob(job: DevProdSyncJob): void {
    this.activeJob.set(job);
    this.manifestJson.set(null);
    this.cleanDryRun.set(this.canUseDryRunForApply(job) ? job : null);
    if (this.isRunningJob(job)) this.pollJob(job.job_id);
  }

  close(): void {
    this.closeModal.emit();
  }

  private pollJob(jobId: string): void {
    if (this.polledJobId === jobId) return;
    this.polledJobId = jobId;

    interval(2500).pipe(
      startWith(0),
      switchMap(() => this.syncService.getJob(jobId).pipe(catchError(() => of(null)))),
      takeWhile(job => !job || job.status === 'queued' || job.status === 'running', true),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(job => {
      if (!job) return;
      this.activeJob.set(job);
      if (this.isCleanDryRun(job)) {
        this.cleanDryRun.set(job);
        this.toast.show(this.copy.dryRunEnabledToast, 'success');
      }
      if (job.mode === 'apply' && job.status === 'completed') {
        this.toast.show(this.copy.applyComplete, 'success');
      }
      if (job.status === 'failed' || job.status === 'blocked' || job.status === 'stale') {
        this.toast.show(job.error ?? this.copy.jobIncomplete, 'error', 7000);
      }
      if (!this.isRunningJob(job)) {
        this.polledJobId = null;
        this.loadJobs();
      }
    });
  }

  private refreshCleanDryRun(jobs: DevProdSyncJob[]): void {
    const latestEligibleDryRun = jobs.find(job => this.canUseDryRunForApply(job, jobs));
    this.cleanDryRun.set(latestEligibleDryRun ?? null);
  }

  private resumeRunningJob(jobs: DevProdSyncJob[]): void {
    const runningJob = jobs.find(job => this.isRunningJob(job));
    if (!runningJob) return;
    this.activeJob.set(runningJob);
    this.manifestJson.set(null);
    this.pollJob(runningJob.job_id);
  }

  private canUseDryRunForApply(job: DevProdSyncJob, jobs = this.jobs()): boolean {
    if (!this.isCleanDryRun(job)) return false;
    const latestCompletedApply = jobs.find(item => item.mode === 'apply' && item.status === 'completed');
    return !latestCompletedApply || new Date(job.created_at) > new Date(latestCompletedApply.created_at);
  }

  private isCleanDryRun(job: DevProdSyncJob): boolean {
    return job.mode === 'dry-run' && job.status === 'completed' && job.apply_eligible;
  }

  private isRunningJob(job: DevProdSyncJob): boolean {
    return job.status === 'queued' || job.status === 'running';
  }

  private readinessErrorMessage(err: any): string {
    if (this.looksLikeHtmlFallbackResponse(err)) return this.copy.readinessHtmlFallback;
    const detail = err?.error?.detail || err?.error?.message;
    if (detail) return `${this.copy.readinessGenericError} ${detail}`;
    if (err?.status === 0) return this.copy.readinessNetwork;
    if (err?.status === 401) return this.copy.readinessUnauthorized;
    if (err?.status === 403) return this.copy.readinessForbidden;
    if (err?.status === 404) return this.copy.readinessNotFound;
    return err?.status ? `${this.copy.readinessGenericError} HTTP ${err.status}.` : this.copy.readinessGenericError;
  }

  private looksLikeHtmlFallbackResponse(err: any): boolean {
    const contentType = err?.headers?.get?.('content-type') ?? err?.headers?.get?.('Content-Type');
    const parts = [
      contentType,
      err?.message,
      err?.error?.message,
      err?.error?.text,
      typeof err?.error === 'string' ? err.error : undefined,
    ]
      .filter(Boolean)
      .map(part => String(part).toLowerCase());

    return parts.some(part =>
      part.includes('text/html')
      || (part.includes('unexpected token') && part.includes('<'))
      || part.includes('not valid json')
      || part.includes('http failure during parsing')
      || part.includes('<!doctype')
      || part.includes('<html')
    );
  }
}
